import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AnySpend component for Collector Club pack purchases
 *
 * This component enables users to purchase Collector Club packs using any token via AnySpend.
 * It calls the `buyPacksFor` function on the Collector Club Shop contract on Base.
 * Uses exact-out flow to ensure the contract receives exactly the required USDC amount.
 *
 * @example
 * ```tsx
 * import { AnySpendCollectorClubPurchase } from "../../..";
 * import { USDC_BASE } from "../../../anyspend/constants/index.js";
 *
 * function MyComponent() {
 *   return (
 *     <AnySpendCollectorClubPurchase
 *       packId={1}
 *       packAmount={5}
 *       pricePerPack="10000" // 0.01 USDC in wei (6 decimals)
 *       paymentToken={USDC_BASE}
 *       recipientAddress="0x123..."
 *       onSuccess={(txHash) => console.log("Purchase successful!", txHash)}
 *     />
 *   );
 * }
 * ```
 */
import { USDC_BASE } from "../../../anyspend/constants/index.js";
import { PUBLIC_BASE_RPC_URL } from "../../../shared/constants/index.js";
import { formatUnits } from "../../../shared/utils/number.js";
import { useEffect, useMemo, useState } from "react";
import { createPublicClient, encodeFunctionData, http } from "viem";
import { base } from "viem/chains";
import { AnySpendCustom } from "./AnySpendCustom.js";
import { BUY_PACKS_FOR_ABI, BUY_PACKS_FOR_WITH_DISCOUNT_ABI, GET_DISCOUNT_CODE_ABI, IS_DISCOUNT_CODE_VALID_FOR_PACK_ABI } from "./ccShopAbi.js";
// Collector Club Shop contract addresses on Base
const CC_SHOP_ADDRESS = "0x47366E64E4917dd4DdC04Fb9DC507c1dD2b87294";
const CC_SHOP_ADDRESS_STAGING = "0x8b751143342ac41eB965E55430e3F7Adf6BE01fA";
const BASE_CHAIN_ID = 8453;
const basePublicClient = createPublicClient({
  chain: base,
  transport: http(PUBLIC_BASE_RPC_URL)
});
export function AnySpendCollectorClubPurchase({
  loadOrder,
  mode = "modal",
  activeTab = "crypto",
  packId,
  packAmount,
  pricePerPack,
  paymentToken = USDC_BASE,
  recipientAddress,
  spenderAddress,
  isStaging = false,
  onSuccess,
  header,
  showRecipient = true,
  vendingMachineId,
  packType,
  forceFiatPayment,
  showFiatOption = true,
  discountCode,
  slots,
  content,
  theme
}) {
  const ccShopAddress = isStaging ? CC_SHOP_ADDRESS_STAGING : CC_SHOP_ADDRESS;
  // Calculate total amount needed (pricePerPack * packAmount)
  const totalAmount = useMemo(() => {
    try {
      return (BigInt(pricePerPack) * BigInt(packAmount)).toString();
    } catch (error) {
      console.error("Failed to calculate total amount from props", {
        pricePerPack,
        packAmount,
        error
      });
      return "0";
    }
  }, [pricePerPack, packAmount]);
  // Discount code validation state
  const [discountInfo, setDiscountInfo] = useState({
    isValid: false,
    discountAmount: BigInt(0),
    minPurchaseAmount: BigInt(0),
    isLoading: false,
    error: null
  });
  // Validate discount code on-chain when provided
  useEffect(() => {
    if (!discountCode) {
      setDiscountInfo({
        isValid: false,
        discountAmount: BigInt(0),
        minPurchaseAmount: BigInt(0),
        isLoading: false,
        error: null
      });
      return;
    }
    let cancelled = false;
    const validateDiscount = async () => {
      setDiscountInfo(prev => ({
        ...prev,
        isLoading: true,
        error: null
      }));
      try {
        // Validate against specific pack and fetch full details in parallel
        const [validForPack, codeDetails] = await Promise.all([basePublicClient.readContract({
          address: ccShopAddress,
          abi: [IS_DISCOUNT_CODE_VALID_FOR_PACK_ABI],
          functionName: "isDiscountCodeValidForPack",
          args: [discountCode, BigInt(packId)]
        }), basePublicClient.readContract({
          address: ccShopAddress,
          abi: [GET_DISCOUNT_CODE_ABI],
          functionName: "getDiscountCode",
          args: [discountCode]
        })]);
        if (cancelled) return;
        const [isValid, discountAmount] = validForPack;
        const {
          minPurchaseAmount,
          packId: restrictedPackId,
          exists
        } = codeDetails;
        if (!exists) {
          setDiscountInfo({
            isValid: false,
            discountAmount: BigInt(0),
            minPurchaseAmount: BigInt(0),
            isLoading: false,
            error: "Discount code does not exist"
          });
          return;
        }
        if (!isValid) {
          // Provide specific error based on code details
          if (restrictedPackId !== BigInt(0) && restrictedPackId !== BigInt(packId)) {
            setDiscountInfo({
              isValid: false,
              discountAmount: BigInt(0),
              minPurchaseAmount: BigInt(0),
              isLoading: false,
              error: "Discount code is not valid for this pack"
            });
          } else {
            setDiscountInfo({
              isValid: false,
              discountAmount: BigInt(0),
              minPurchaseAmount: BigInt(0),
              isLoading: false,
              error: "Invalid or expired discount code"
            });
          }
          return;
        }
        setDiscountInfo({
          isValid: true,
          discountAmount,
          minPurchaseAmount,
          isLoading: false,
          error: null
        });
      } catch (error_0) {
        if (cancelled) return;
        console.error("Failed to validate discount code", {
          discountCode,
          error: error_0
        });
        setDiscountInfo({
          isValid: false,
          discountAmount: BigInt(0),
          minPurchaseAmount: BigInt(0),
          isLoading: false,
          error: "Failed to validate discount code"
        });
      }
    };
    validateDiscount();
    return () => {
      cancelled = true;
    };
  }, [discountCode, ccShopAddress, packId]);
  // Calculate effective dstAmount after discount
  const effectiveDstAmount = useMemo(() => {
    if (!discountCode || !discountInfo.isValid || discountInfo.discountAmount === BigInt(0)) {
      return totalAmount;
    }
    const total = BigInt(totalAmount);
    const discount = discountInfo.discountAmount;
    if (discount >= total) {
      console.error("Discount exceeds total price", {
        totalAmount,
        discountAmount: discount.toString()
      });
      return "0";
    }
    return (total - discount).toString();
  }, [totalAmount, discountCode, discountInfo.isValid, discountInfo.discountAmount]);
  // Calculate fiat amount (effectiveDstAmount in USD, assuming USDC with 6 decimals)
  const srcFiatAmount = useMemo(() => {
    if (!effectiveDstAmount || effectiveDstAmount === "0") return "0";
    return formatUnits(effectiveDstAmount, USDC_BASE.decimals);
  }, [effectiveDstAmount]);
  // Encode the contract function call (with or without discount)
  const encodedData = useMemo(() => {
    try {
      if (discountCode && discountInfo.isValid) {
        return encodeFunctionData({
          abi: [BUY_PACKS_FOR_WITH_DISCOUNT_ABI],
          functionName: "buyPacksForWithDiscount",
          args: [recipientAddress, BigInt(packId), BigInt(packAmount), discountCode]
        });
      }
      return encodeFunctionData({
        abi: [BUY_PACKS_FOR_ABI],
        functionName: "buyPacksFor",
        args: [recipientAddress, BigInt(packId), BigInt(packAmount)]
      });
    } catch (error_1) {
      console.error("Failed to encode function data", {
        recipientAddress,
        packId,
        packAmount,
        discountCode,
        error: error_1
      });
      return "0x";
    }
  }, [recipientAddress, packId, packAmount, discountCode, discountInfo.isValid]);
  // Default header if not provided
  const defaultHeader = () => _jsx("div", {
    className: "mb-4 flex flex-col items-center gap-3 text-center",
    children: _jsxs("div", {
      children: [_jsx("h1", {
        className: "text-as-primary text-xl font-bold",
        children: "Buy Collector Club Packs"
      }), _jsxs("p", {
        className: "text-as-secondary text-sm",
        children: ["Purchase ", packAmount, " pack", packAmount !== 1 ? "s" : "", " using any token"]
      })]
    })
  });
  // Don't render AnySpendCustom while discount is being validated (avoids showing wrong price)
  if (discountCode && discountInfo.isLoading) {
    return _jsx("div", {
      className: "mb-4 flex flex-col items-center gap-3 text-center",
      children: _jsx("p", {
        className: "text-as-secondary text-sm",
        children: "Validating discount code..."
      })
    });
  }
  if (discountCode && discountInfo.error) {
    return _jsx("div", {
      className: "mb-4 flex flex-col items-center gap-3 text-center",
      children: _jsx("p", {
        className: "text-sm text-red-500",
        children: discountInfo.error
      })
    });
  }
  if (discountCode && discountInfo.isValid && discountInfo.minPurchaseAmount > BigInt(0) && BigInt(packAmount) < discountInfo.minPurchaseAmount) {
    return _jsx("div", {
      className: "mb-4 flex flex-col items-center gap-3 text-center",
      children: _jsxs("p", {
        className: "text-sm text-red-500",
        children: ["Minimum purchase of ", discountInfo.minPurchaseAmount.toString(), " pack", discountInfo.minPurchaseAmount > BigInt(1) ? "s" : "", " required for this discount code"]
      })
    });
  }
  if (discountCode && discountInfo.isValid && effectiveDstAmount === "0") {
    return _jsx("div", {
      className: "mb-4 flex flex-col items-center gap-3 text-center",
      children: _jsx("p", {
        className: "text-sm text-red-500",
        children: "Discount exceeds total price"
      })
    });
  }
  return _jsx(AnySpendCustom, {
    loadOrder: loadOrder,
    mode: mode,
    activeTab: activeTab,
    recipientAddress: recipientAddress,
    spenderAddress: spenderAddress ?? ccShopAddress,
    orderType: "custom",
    dstChainId: BASE_CHAIN_ID,
    dstToken: paymentToken,
    dstAmount: effectiveDstAmount,
    contractAddress: ccShopAddress,
    encodedData: encodedData,
    metadata: {
      packId,
      packAmount,
      pricePerPack,
      vendingMachineId,
      packType,
      ...(discountCode && discountInfo.isValid ? {
        discountCode,
        discountAmount: discountInfo.discountAmount.toString()
      } : {})
    },
    header: header || defaultHeader,
    onSuccess: onSuccess,
    showRecipient: showRecipient,
    srcFiatAmount: srcFiatAmount,
    forceFiatPayment: forceFiatPayment,
    showFiatOption: showFiatOption,
    slots: slots,
    content: content,
    theme: theme
  });
}